import React, { useState, useEffect, useRef } from "react";

interface TableCell {
    value: string;
}

interface TableRow {
    cells: TableCell[];
}

interface TableComponentProps {
    sectionId: number;
    initialData?: string[]; // each item is column HTML (content1, content2...)
    onChange: (id: number, updatedData: { bodyCopy: string[] }) => void;
    onClose?: () => void;
}

const TableComponent: React.FC<TableComponentProps> = ({
                                                           sectionId,
                                                           initialData = [],
                                                           onChange,
                                                           onClose,
                                                       }) => {
    // Refs to store previous columnHTMLs to avoid unnecessary onChange calls
    const prevColumnHTMLs = useRef<string[] | null>(null);

    const [columns, setColumns] = useState<number>(() => initialData.length || 2);


    const [headers, setHeaders] = useState<string[]>(() => {
        if (!initialData.length) return Array(2).fill("");
        return initialData.map((html) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const th = doc.querySelector("th");
            return th ? th.textContent || "" : "";
        });
    });

    const [rows, setRows] = useState<TableRow[]>(() => {
        if (!initialData.length) return [{ cells: Array(2).fill({ value: "" }) }];
        const numCols = initialData.length;
        const parsedRows: TableRow[] = [];
        initialData.forEach((colHtml, colIndex) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(colHtml, "text/html");
            const tds = Array.from(doc.querySelectorAll("tbody tr td")).map(
                (td) => td.textContent || ""
            );
            tds.forEach((cellValue, rowIndex) => {
                if (!parsedRows[rowIndex])
                    parsedRows[rowIndex] = { cells: Array(numCols).fill({ value: "" }) };
                parsedRows[rowIndex].cells[colIndex] = { value: cellValue };
            });
        });
        return parsedRows;
    });

    // Generate HTML and call onChange whenever headers/rows/columns change
    useEffect(() => {
        const columnHTMLs = Array(columns)
            .fill("")
            .map((_, colIndex) => {
                let html = "<table style='border-collapse: collapse; width:100%;'>";
                html += `<thead><tr><th style="font-weight:bold;">${
                    headers[colIndex] || ""
                }</th></tr></thead><tbody>`;
                rows.forEach((row) => {
                    html += `<tr><td>${row.cells[colIndex]?.value || ""}</td></tr>`;
                });
                html += "</tbody></table>";
                return html;
            });

        // Avoid calling onChange if HTML hasn't changed
        if (
            !prevColumnHTMLs.current ||
            prevColumnHTMLs.current.join() !== columnHTMLs.join()
        ) {
            prevColumnHTMLs.current = columnHTMLs;
            onChange(sectionId, { bodyCopy: columnHTMLs });
        }
    }, [headers, rows, columns, onChange, sectionId]);

    const handleHeaderChange = (index: number, value: string) => {
        setHeaders((prev) => prev.map((h, i) => (i === index ? value : h)));
    };

    const handleCellChange = (rowIndex: number, cellIndex: number, value: string) => {
        setRows((prev) => {
            const newRows = [...prev];
            newRows[rowIndex].cells[cellIndex] = { value };
            return newRows;
        });
    };

    const addRow = () => {
        setRows((prev) => [...prev, { cells: Array(columns).fill({ value: "" }) }]);
    };

    const removeLastRow = () => {
        setRows((prev) => prev.slice(0, prev.length - 1));
    };

    const handleColumnChange = (newColumnCount: number) => {
        setColumns(newColumnCount);

        // Adjust headers
        setHeaders((prev) => {
            const newHeaders = Array(newColumnCount).fill("");
            prev.forEach((h, i) => {
                if (i < newColumnCount) newHeaders[i] = h;
            });
            return newHeaders;
        });

        // Adjust rows
        setRows((prev) =>
            prev.map((row) => {
                const newCells = Array(newColumnCount).fill({ value: "" });
                row.cells.forEach((cell, i) => {
                    if (i < newColumnCount) newCells[i] = cell;
                });
                return { cells: newCells };
            })
        );
    };

    return (
        <div
            style={{
                border: "1px solid #ccc",
                padding: "10px",
                marginBottom: "20px",
                borderRadius: "5px",
                position: "relative",
                width: "100%",
            }}
        >
            {onClose && (
                <button
                    onClick={onClose}
                    style={{
                        position: "absolute",
                        top: 5,
                        right: 5,
                        border: "none",
                        background: "none",
                        color: "black",
                        cursor: "pointer",
                    }}
                >
                    X
                </button>
            )}

            {/* Column selector */}
            <div style={{ marginBottom: "10px" }}>
                <label>Columns: </label>
                <select
                    value={columns}
                    onChange={(e) => handleColumnChange(Number(e.target.value))}
                >
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                </select>
            </div>

            {/* Editable headers */}
            <div style={{ display: "flex", gap: "5px", marginBottom: "10px" }}>
                {headers.map((h, i) => (
                    <input
                        key={i}
                        type="text"
                        value={h}
                        placeholder={`Header ${i + 1}`}
                        onChange={(e) => handleHeaderChange(i, e.target.value)}
                        style={{ flex: 1, padding: "5px", fontWeight: "bold" }}
                    />
                ))}
            </div>

            {/* Rows */}
            {rows.map((row, rowIndex) => (
                <div
                    key={rowIndex}
                    style={{ display: "flex", gap: "5px", marginBottom: "5px" }}
                >
                    {row.cells.map((cell, cellIndex) => (
                        <input
                            key={cellIndex}
                            type="text"
                            value={cell.value}
                            placeholder={`R${rowIndex + 1}C${cellIndex + 1}`}
                            onChange={(e) =>
                                handleCellChange(rowIndex, cellIndex, e.target.value)
                            }
                            style={{ flex: 1, padding: "5px" }}
                        />
                    ))}
                </div>
            ))}

            {/* Row controls */}
            <div
                style={{
                    marginTop: "10px",
                    display: "flex",
                    gap: "10px",
                    justifyContent: "center",
                }}
            >
                <button type="button" onClick={addRow}>
                    Add Row
                </button>
                <button type="button" onClick={removeLastRow} disabled={rows.length === 0}>
                    Remove Last Row
                </button>
            </div>

            {/* Preview */}
            <div
                style={{
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    gap: "20px",
                    width: "100%",
                    flexWrap: "wrap",
                }}
            >
                {Array(columns)
                    .fill("")
                    .map((_, i) => (
                        <div
                            key={i}
                            style={{ flex: `1`, maxWidth: `${100 / columns}%`, boxSizing: "border-box" }}
                            dangerouslySetInnerHTML={{
                                __html: rows.length
                                    ? `<table style="border-collapse: collapse; width:100%;"><thead><tr><th style="font-weight:bold;">${headers[i] || ""
                                    }</th></tr></thead><tbody>${rows
                                        .map((row) => `<tr><td>${row.cells[i]?.value || ""}</td></tr>`)
                                        .join("")}</tbody></table>`
                                    : "",
                            }}
                        />
                    ))}
            </div>
        </div>
    );
};

export default TableComponent;
